import { Button, Card, Form, FormButtonRow, FormGroup, PageHeader } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { DateInput, Dropdown } from '../../elements/common';

export function FundusNoShowsExportRequests() {
    const [examinationStartDate, setExaminationStartDate] = useState<string | null>(null);
    const [examinationEndDate, setExaminationEndDate] = useState<string | null>(null);
    const [requestStartDate, setRequestStartDate] = useState<string | null>(null);
    const [requestEndDate, setRequestEndDate] = useState<string | null>(null);
    const [requestGroupExaminations, setRequestGroupExaminations] = useState<string | null>(null);
    const [requestGroupRequests, setRequestGroupRequests] = useState<string | null>(null);
    const [requesterGroups, setRequesterGroups] = useState<{ text: string; value: string }[]>([]);
    assertHasAuthorization(ManagerAuthorization.exportFundusFiles);

    useEffect(() => {
        loadCareGroups();
    }, []);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey('export.exportFundusFiles')} />
            <Form
                onSubmit={async (event) => {
                    event.preventDefault();
                    exportRequests(examinationStartDate, examinationEndDate, false);
                }}
                submitOnEnter={false}
            >
                <Card>
                    <Card.Header
                        title={translateKey('export.exportFundusExaminations')}
                        tooltipContent="Alle fundus onderzoeken die zijn beoordeeld en verstuurd in opgegeven periode"
                    />
                    <Card.Body>
                        <FormGroup label={translateKey('common.startDate')}>
                            <DateInput
                                hasMaxWidth={true}
                                onChange={(value) => setExaminationStartDate(value)}
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={examinationStartDate}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup label={translateKey('common.endDate')}>
                            <DateInput
                                hasMaxWidth={true}
                                onChange={(value) => setExaminationEndDate(value)}
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={examinationEndDate}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup label="Zorggroep aanvrager">
                            <Dropdown
                                automation="ZorggroepAanvragerExportFundusFiles"
                                onChange={(value) => setRequestGroupExaminations(value)}
                                placeholder="Zorggroep aanvrager"
                                selectOptions={requesterGroups}
                                value={requestGroupExaminations}
                            />
                        </FormGroup>
                        <FormButtonRow>
                            <Button
                                color="primary"
                                type="submit"
                            >
                                {translateKey('export.export')}
                            </Button>
                        </FormButtonRow>
                    </Card.Body>
                </Card>
            </Form>

            <Form
                onSubmit={async (event) => {
                    event.preventDefault();
                    exportRequests(requestStartDate, requestEndDate, true);
                }}
                submitOnEnter={false}
            >
                <Card>
                    <Card.Header
                        title={translateKey('export.exportFundusRequests')}
                        tooltipContent="Alle fundusaanvragen die zijn verzonden in de opgegeven periode"
                    />
                    <Card.Body>
                        <FormGroup label={translateKey('common.startDate')}>
                            <DateInput
                                hasMaxWidth={true}
                                onChange={(value) => setRequestStartDate(value)}
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={requestStartDate}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup label={translateKey('common.endDate')}>
                            <DateInput
                                hasMaxWidth={true}
                                onChange={(value) => setRequestEndDate(value)}
                                placeholder={translateKey('common.dateFormat')}
                                size="large"
                                value={requestEndDate}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup label="Zorggroep aanvrager">
                            <Dropdown
                                automation="ZorggroepAanvragerExportRequestFiles"
                                onChange={(value) => setRequestGroupRequests(value)}
                                placeholder="Zorggroep aanvrager"
                                selectOptions={requesterGroups}
                                value={requestGroupRequests}
                            />
                        </FormGroup>
                        <FormButtonRow>
                            <Button
                                color="primary"
                                type="submit"
                            >
                                {translateKey('export.export')}
                            </Button>
                        </FormButtonRow>
                    </Card.Body>
                </Card>
            </Form>
        </PageTemplate>
    );
    async function loadCareGroups() {
        const result = await fetchJson('/api/groups/getAllActiveForTfo', 'GET');
        if (result.json) {
            setRequesterGroups(
                result.json.requesterGroups.map((group: { id: string; name: string }) => {
                    return { text: group.name, value: group.id };
                })
            );
        }
    }

    function exportRequests(startDate: string | null, endDate: string | null, onlyRequests?: boolean) {
        let url = `/api/requests/exportTfoData?onlyRequests=${onlyRequests || false}`;

        if (startDate) {
            url += `&startDate=${encodeURIComponent(startDate)}`;
        }

        if (endDate) {
            url += `&endDate=${encodeURIComponent(endDate)}`;
        }

        const requesterGroupId = onlyRequests ? requestGroupRequests : requestGroupExaminations;
        if (requesterGroupId) {
            url += `&requesterGroupId=${encodeURIComponent(requesterGroupId)}`;
        }

        window.open(url);
    }
}
